import { Component, inject, OnInit, Input, ChangeDetectorRef } from '@angular/core';

import { AuthProviderService } from '../../core/services/auth-provider.service';
import { AuthProvider } from '../../core/models/auth-provider';
import { PlatformService } from '../../core/services/platform.service';

/**
 * Reusable social login buttons component.
 *
 * Renders "Sign in with Google", "Sign in with Microsoft", and any other
 * enabled external auth providers. New providers appear automatically
 * when added to the AuthProviders database table.
 *
 * Respects the ExternalAuthProviders feature toggle — when disabled,
 * this component renders nothing.
 *
 * Usage:
 *   <app-social-login-buttons
 *     mode="login"
 *     redirectUri="http://localhost:4200/auth/callback">
 *   </app-social-login-buttons>
 *
 * @Input mode - 'login' | 'register' | 'link' — controls the CTA text
 * @Input redirectUri - The OAuth redirect URI for the callback
 */
@Component({
  selector: 'app-social-login-buttons',
  standalone: true,
  imports: [],
  template: `
    @if (isFeatureEnabled && providers.length > 0) {
      <div class="social-login-section">
        <div class="divider">
          <span class="divider-text">{{ dividerText }}</span>
        </div>
        <div class="social-buttons">
          @for (provider of providers; track provider) {
            <button
              class="btn-social"
              [class]="'btn-' + provider.providerKey"
              [title]="provider.displayName"
              (click)="onProviderClick(provider)"
              [disabled]="isLoading"
              >
              @if (provider.iconUrl) {
                <img
                  [src]="provider.iconUrl"
                  [alt]="provider.displayName"
                  class="provider-icon"
                  width="20"
                  height="20"
                  />
              }
              @if (!provider.iconUrl) {
                <i class="fas fa-external-link-alt"></i>
              }
              <span>{{ buttonLabel(provider) }}</span>
            </button>
          }
        </div>
      </div>
    }
    `,
  styles: [`
    .social-login-section {
      margin: 20px 0;
    }

    .divider {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 20px 0;
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #ddd;
    }

    .divider-text {
      padding: 0 12px;
      color: #888;
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .social-buttons {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .btn-social {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      padding: 12px 16px;
      border: 2px solid #ddd;
      border-radius: 8px;
      background: #fff;
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #333;
    }

    .btn-social:hover:not(:disabled) {
      border-color: #0056a7;
      background: #f0f7ff;
    }

    .btn-social:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-google {
      border-color: #ea4335;
    }

    .btn-google:hover:not(:disabled) {
      background: #fef3f1;
    }

    .btn-microsoft {
      border-color: #00a4ef;
    }

    .btn-microsoft:hover:not(:disabled) {
      background: #f0f9ff;
    }

    .provider-icon {
      flex-shrink: 0;
    }

    @media (max-width: 480px) {
      .btn-social {
        padding: 10px 14px;
        font-size: 0.9rem;
      }
    }
  `],
})
export class SocialLoginButtonsComponent implements OnInit {
  @Input() mode: 'login' | 'register' | 'link' = 'login';
  @Input() redirectUri: string = '';

  private readonly authProviderService = inject(AuthProviderService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platform = inject(PlatformService);

  providers: AuthProvider[] = [];
  isLoading = false;
  isFeatureEnabled = false;

  ngOnInit(): void {
    // OAuth redirects and provider discovery are browser-only. Avoid both
    // during server-side rendering, where `window` and session storage do not
    // exist. The component will initialize normally during client hydration.
    if (!this.platform.isBrowser) {
      return;
    }

    // `feature-toggles` is an administration endpoint and returns 403 for
    // anonymous visitors on the login and registration pages. The public
    // auth-providers endpoint is the source of truth here: when external
    // authentication is disabled, it returns no enabled providers.
    this.authProviderService.loadProviders().subscribe((providers) => {
      if (Array.isArray(providers)) {
        this.providers = providers.filter((provider) => provider.isEnabled);
        this.isFeatureEnabled = this.providers.length > 0;
      } else {
        this.providers = [];
        this.isFeatureEnabled = false;
      }
      this.cdr.detectChanges();
    });

    // Set default redirect URI if not provided.
    if (!this.redirectUri) {
      this.redirectUri = `${window.location.origin}/auth/callback`;
    }
  }

  get dividerText(): string {
    switch (this.mode) {
      case 'register':
        return 'Or sign up with';
      case 'link':
        return 'Or link with';
      case 'login':
      default:
        return 'Or continue with';
    }
  }

  buttonLabel(provider: AuthProvider): string {
    switch (this.mode) {
      case 'register':
        return `Sign up with ${provider.name}`;
      case 'link':
        return `Link ${provider.name}`;
      case 'login':
      default:
        return provider.displayName;
    }
  }

  onProviderClick(provider: AuthProvider): void {
    this.isLoading = true;
    this.authProviderService.initiateOAuthFlow(provider, this.redirectUri).catch((err) => {
      console.error('Failed to initiate OAuth flow:', err);
      this.isLoading = false;
    });
  }
}
